import { Component, ChangeDetectionStrategy, inject, DestroyRef, OnInit, ChangeDetectorRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
 import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { RouterLink, Router } from '@angular/router';
import { KeycloakService } from '../../../core/auth/keycloak.service';
import { LocaleService } from '../../../core/services/locale.service';
import { NotificationService } from '../../../core/services/notification.service';
import { ProfileService } from '../../services/profile.service';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-header-actions',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
     MatTooltipModule,
    MatBadgeModule,
    RouterLink],
  templateUrl: './header-actions.component.html',
  styleUrl: './header-actions.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderActionsComponent implements OnInit {
  private keycloakService = inject(KeycloakService);
  private destroyRef = inject(DestroyRef);
  localeService = inject(LocaleService);
  notificationService = inject(NotificationService);
  profileService = inject(ProfileService);

  private cdr = inject(ChangeDetectorRef);

  userFullName = '';
  userEmail = '';
  userInitials = '';
  isHindiActive = false;
  private router = inject(Router);

  ngOnInit(): void {
    this.keycloakService.userContextAuthoritative$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ctx: any | null) => {
        if (!ctx) return;
        this.userFullName = ctx.fullName || ctx.username || '';
        this.userEmail = ctx.email || '';
        this.userInitials = this.buildInitials(this.userFullName);
        this.cdr.markForCheck();
      });

    this.isHindiActive = this.localeService.isHindi();
    this.profileService.ensureLoaded();
  }

  private readonly validModules = ['salary', 'budget', 'cash', 'accounting', 'grants', 'advances', 'cpf', 'assets', 'audit', 'admin', 'vendor-management'];

  /** The module the user is currently inside, or '' on the landing/global pages. */
  currentModule(): string {
    const segment = this.router.url.split('/')[1]?.split('?')[0] ?? '';
    return this.validModules.includes(segment) ? segment : '';
  }

  unreadCount(): number {
    return this.notificationService.getUnreadCount(this.currentModule() || undefined);
  }

  navigateToNotifications(): void {
    const url = this.router.url;
    const segments = url.split('/');
    const currentModule = segments[1];
    const validModules = ['salary', 'budget', 'cash', 'accounting', 'grants', 'advances', 'cpf', 'assets', 'audit', 'admin', 'vendor-management'];
    
    if (validModules.includes(currentModule)) {
      this.router.navigate([`/${currentModule}/notifications`]);
    } else {
      this.router.navigate(['/notifications']);
    }
  }

  toggleLanguage(): void {
    this.isHindiActive = !this.isHindiActive;
    this.cdr.markForCheck();
    
    setTimeout(() => {
      if (this.isHindiActive) {
        this.localeService.switchToHindi();
      } else {
        this.localeService.switchToEnglish();
      }
    }, 300);
  }

  logout(): void {
    this.keycloakService.logout();
  }

  private buildInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0][0]?.toUpperCase() || '?';
  }
}
